/**
 * Settings & initialization actions — group, period, viewing learner, dev/demo.
 *
 * Extracted from store.ts as part of T1 (store.ts opsplitsen).
 */

import type { SetFn, GetFn, AppStoreState } from './types'
import type { GroupId, StartingPeriod } from '../../config/systemConfig'
import { GROUPS } from '../../config/systemConfig'
import { DEFAULT_GROUP } from '../../config/constants'
import {
  ensureAdminAccount,
  loadState,
  saveState,
  createFreshState,
} from '../../accounts'
import { dataLayer } from '../../data/dataLayer'
import { debug, error as logError, warn } from '../../utils/logger'
import { loadDemoState, seedDemoAssessmentsForState } from '../../demo'

type SettingsActions = Pick<
  AppStoreState,
  | 'initialize'
  | 'setSelectedGroup'
  | 'setStartingPeriod'
  | 'setViewingLearner'
  | 'devResetProgress'
  | 'loadDemoAccounts'
>

export function createSettingsActions(set: SetFn, get: GetFn): SettingsActions {
  return {
    initialize: async () => {
      const state = get()
      if (state.isInitialized) return

      // Load persisted group/period from dataLayer
      try {
        const storedGroup = await dataLayer.getGroup()
        if (storedGroup) {
          const parsed = storedGroup as GroupId
          if (GROUPS.includes(parsed)) {
            set({ selectedGroup: parsed })
          }
        }
        const storedPeriod = await dataLayer.getPeriod()
        if (storedPeriod) {
          set({ manualPeriod: storedPeriod as StartingPeriod })
        }
      } catch {
        // Ignore parse errors
      }

      // Load multi-account state (V4 only)
      const loadedState = await loadState()
      const migratedState = {
        ...loadedState,
        accounts: ensureAdminAccount(loadedState.accounts),
      }
      saveState(migratedState)
      set({ multiAccountState: migratedState, isInitialized: true })
    },

    /**
     * Explicit override of the active group.
     *
     * ⚠️ For most reads, prefer `deriveSelectedGroup(state)` or the
     * `selectedGroup` value exposed by `useAppStore` — both auto-derive
     * from learner.grade / viewing learner context.
     *
     * Use this action only for legitimate overrides such as:
     * - Admin "preview as group N" tooling
     * - Loading demo state (where the active demo learner's grade is applied)
     *
     * Routine sync from learner.grade is handled automatically; calling this
     * directly to "switch the user to another group" is almost always a bug.
     */
    setSelectedGroup: (group) => {
      if (!GROUPS.includes(group)) {
        warn(`Invalid group "${group}" — ignoring setSelectedGroup call`)
        return
      }
      set((s) => ({
        selectedGroup: group,
        groupVersion: s.groupVersion + 1,
      }))
      dataLayer.setGroup(group)
      debug(`Group switched to ${group}`)
    },

    setStartingPeriod: (period) => {
      set((s) => ({
        manualPeriod: period,
        groupVersion: s.groupVersion + 1,
      }))
      if (period) {
        dataLayer.setPeriod(period)
      } else {
        dataLayer.clearPeriod()
      }
      debug(`Starting period set to ${period || 'AUTO'} - context updated`)
    },

    setViewingLearner: (learnerId) => {
      set({ viewingLearnerId: learnerId })
      debug(`Viewing learner set to: ${learnerId || 'none'}`)
    },

    devResetProgress: () => {
      const freshState = createFreshState()
      set((s) => ({
        multiAccountState: freshState,
        groupVersion: s.groupVersion + 1,
      }))
      saveState(freshState)
    },

    loadDemoAccounts: async () => {
      // Demo path: ensure content is loaded, then generate profiles + assessments.
      // This is the ONE path that requires all 10 modules upfront.
      const demoState = await loadDemoState()
      const multiAccountState = demoState.multiAccountState

      // Djayden (grade 7) is the active account — sync selectedGroup
      const activeId = multiAccountState.accounts.activeAccountId
      const activeAccount = multiAccountState.accounts.accountsById[activeId]
      const activeGrade =
        activeAccount?.role === 'learner' && 'grade' in activeAccount
          ? (activeAccount as { grade?: number }).grade
          : undefined
      const groupToSet =
        activeGrade && GROUPS.includes(activeGrade as GroupId)
          ? (activeGrade as GroupId)
          : DEFAULT_GROUP
      set((s) => ({
        multiAccountState,
        selectedGroup: groupToSet,
        groupVersion: s.groupVersion + 1,
      }))
      dataLayer.setGroup(groupToSet)
      saveState(multiAccountState)

      // Pick a teacher + classroom for assessment seeding
      const meesterJay = Object.values(
        multiAccountState.accounts.accountsById,
      ).find((a) => a.displayName === 'Meester Jay')
      const firstClassroomId = Object.keys(
        multiAccountState.accounts.classrooms || {},
      )[0]

      try {
        await seedDemoAssessmentsForState(demoState, {
          teacherId: meesterJay?.id,
          classroomId: firstClassroomId,
        })
      } catch (err) {
        logError('Failed to seed demo assessments', err)
      }

      debug('✅ Demo accounts loaded successfully')
    },
  }
}
